import React, { useEffect, useRef, useState } from 'react';
import { PRESETS, useRange } from '../range';

/**
 * The panel's single time control, in the top bar rather than per-page.
 *
 * Putting it in the shell is the point: a range that belongs to a page is a
 * range you have to re-set on every page, and one you can't trust to match
 * the page you just came from.
 */
export default function RangePicker() {
  const range = useRange();
  const [open, setOpen] = useState(false);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const applyCustom = () => {
    if (!since || !until) return;
    const s = new Date(since);
    const u = new Date(until);
    if (s >= u) return;
    range.setCustom(s.toISOString(), u.toISOString());
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg border border-hairline bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-surface-page"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="text-ink-muted" aria-hidden="true">
          <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M2 6.5H14" stroke="currentColor" strokeWidth="1.3" />
          <path d="M5 1.5V4M11 1.5V4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        {range.label}
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-72 overflow-hidden rounded-xl border border-hairline bg-surface shadow-lg">
          <div className="py-1">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => { range.setPreset(p.key); setOpen(false); }}
                className="flex w-full items-center justify-between px-3.5 py-2 text-left text-sm text-ink hover:bg-surface-page"
              >
                {p.label}
                {range.key === p.key && (
                  <svg width="16" height="16" viewBox="0 0 16 16" className="text-accent" aria-hidden="true">
                    <path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-hairline px-3.5 py-3">
            <div className="mb-2 flex items-center justify-between text-xs font-medium text-ink-secondary">
              Custom range
              {range.key === 'custom' && (
                <svg width="16" height="16" viewBox="0 0 16 16" className="text-accent" aria-hidden="true">
                  <path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <input
                type="datetime-local"
                value={since}
                max={until || undefined}
                onChange={(e) => setSince(e.target.value)}
                className="rounded-md border border-hairline px-2 py-1.5 text-xs text-ink"
              />
              <input
                type="datetime-local"
                value={until}
                min={since || undefined}
                onChange={(e) => setUntil(e.target.value)}
                className="rounded-md border border-hairline px-2 py-1.5 text-xs text-ink"
              />
              <button
                type="button"
                onClick={applyCustom}
                disabled={!since || !until}
                className="mt-1 rounded-md bg-ink px-2 py-1.5 text-xs font-medium text-white disabled:opacity-40"
              >
                Apply
              </button>
            </div>
            {/* Endpoints that take `days` rather than a timestamp pair get a
                derived, clamped day count — worth stating so a 200-day custom
                range doesn't silently read as 180 on half the pages. */}
            {range.key === 'custom' && (
              <p className="mt-2 text-[11px] leading-snug text-ink-muted">
                Sections that aggregate by day will read this as {range.days} day{range.days === 1 ? '' : 's'}.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
